import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import BrandName from "./BrandName";
import { APPLY_URL, NAV_LINKS, MOBILE_NAV_LINKS } from "../../lib/site-data";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const closeMenu = () => setOpen(false);

  const scrollToSection = (e, href) => {
    const target = document.querySelector(href);
    if (!target) return;
    e.preventDefault();
    closeMenu();
    const headerH = window.innerWidth >= 768 ? 80 : 64;
    const top = target.getBoundingClientRect().top + window.scrollY - headerH - 40;
    requestAnimationFrame(() => window.scrollTo({ top: Math.max(0, top), behavior: "smooth" }));
    if (window.history?.replaceState) window.history.replaceState(null, "", href);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "backdrop-blur-xl bg-white/90 border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20 gap-3">
        <a
          href="#top"
          data-testid="nav-logo"
          className="group inline-flex items-center min-w-0"
          onClick={closeMenu}
        >
          <span
            className={`font-display font-bold tracking-tight transition-colors text-[15px] sm:text-base md:text-[17px] truncate ${
              scrolled || open ? "text-black" : "text-white"
            }`}
            style={{ letterSpacing: "-0.015em" }}
          >
            <BrandName />
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? "text-black/70 hover:text-black"
                  : "text-white/75 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Link
            to={APPLY_URL}
            data-testid="nav-apply-button"
            className="inline-flex items-center gap-1.5 sm:gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-full transition-all duration-200 whitespace-nowrap px-3.5 py-2 text-[12px] sm:text-[13px] md:px-5 md:py-2.5 md:text-sm shadow-[0_6px_18px_-8px_rgba(234,88,12,0.55)]"
          >
            Become a Member
            <ArrowUpRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
          </Link>
          <button
            className={`inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full border transition-colors ${
              scrolled || open
                ? "border-black/10 text-black"
                : "border-white/20 text-white"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-menu-toggle"
          >
            {open ? <X className="w-4.5 h-4.5" /> : <Menu className="w-4.5 h-4.5" />}
          </button>
        </div>
      </div>

      {/* Full navigation panel (used by hamburger on desktop + mobile) */}
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          open ? "max-h-[88vh] opacity-100" : "max-h-0 opacity-0"
        } bg-white border-b border-black/5`}
        data-testid="nav-mobile-menu"
      >
        <div className="container-x py-4 flex flex-col gap-0 max-h-[80vh] overflow-y-auto">
          {MOBILE_NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => scrollToSection(e, l.href)}
              data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
              className="py-3 text-base font-medium text-black/80 hover:text-orange-600 border-b border-black/5 last:border-0"
            >
              {l.label}
            </a>
          ))}
          <Link
            to={APPLY_URL}
            onClick={closeMenu}
            data-testid="nav-mobile-apply-button"
            className="btn-primary mt-4 mb-2"
          >
            Become a Member
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </header>
  );
}
